import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const cardVariants = cva("text-card-foreground rounded-lg border shadow-elevated", {
  variants: {
    variant: {
      // Default — neutral card surface, white bg, hairline border.
      default: "bg-card",
      // Soft coral wash — for highlighted or brand-tinted info cards.
      "tint-rose": "border-brand-tint-border bg-brand-tint text-brand-tint-foreground",
      // Mint wash — for positive/success info cards. Shares the success
      // palette with StatCard tone="success" and Badge variant="success".
      "tint-mint": "border-success-border bg-success-muted",
      // KPI layout — neutral surface with the tighter padding + flex-col
      // spacing the mockup uses for stat tiles. Tone (mint/neutral) is
      // applied via className by the consumer so kpi composes cleanly
      // with both default and tint surfaces.
      kpi: "flex flex-col gap-2 bg-card p-4",
    },
    // Left-edge accent (CHP-522) — a 4px coloured bar down the card's left
    // edge in a single semantic lane. Drives the feed-card treatment (strong
    // edge by category) and metric-card "colour by exception" (e.g. a subtle
    // amber edge on a pending-registrations tile). The card surface stays
    // neutral; only the edge carries the colour, so cards never "shout".
    accent: {
      none: "",
      coral: "border-l-4 border-l-primary",
      amber: "border-l-4 border-l-warning",
      // CHP-877: a stronger "urgent / mandatory" lane (e.g. outstanding
      // compulsory training) — a destructive/red left edge.
      red: "border-l-4 border-l-destructive",
      success: "border-l-4 border-l-success",
      info: "border-l-4 border-l-info",
      neutral: "border-l-4 border-l-muted-foreground/40",
    },
  },
  defaultVariants: { variant: "default", accent: "none" },
});

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, accent, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, accent }), className)} {...props} />
  ),
);
Card.displayName = "Card";

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-5", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Heading level to render. Defaults to `h3` (the card-title default). Set
   * explicitly to keep a page's heading outline contiguous — e.g. a settings
   * section under the page `<h1>` passes `as="h2"`, a standalone stub route
   * with no other heading passes `as="h1"` (CHP-805).
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Comp = "h3", ...props }, ref) => (
    <Comp
      ref={ref}
      className={cn("font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-5 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-5 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { cardVariants };
